import * as React from "react";
import { isAdmin } from "../../utils/utils";
import ActionMenuButton from "../ActionMenuButton/ActionMenuButton";
import BulkUploadDialog from "./BulkUploadDialog";
import uploadIcon from "../../images/upload.svg";
import styles from "./BulkUploadControl.module.scss";

export type BulkUploadControlProps = {
  /** The domain group of the fault page hosting this control. */
  domainGroup: Api.GroupName;
  /** The current user. Enablement is gated by this user's write role. */
  user: Api.User;
};

/**
 * Bulk Upload control shown in the fault page action menu, next to Add / Table
 * Settings.
 *
 * The control is enabled if and only if the user holds the `admin` role
 * (as computed by {@link isAdmin}). Clicking an enabled control opens the
 * {@link BulkUploadDialog} bound to the page's domain group. It is styled to
 * match the other action-menu buttons (icon + capitalized label),
 * disabled-looking when the user lacks access.
 */
const BulkUploadControl: React.FC<BulkUploadControlProps> = ({ domainGroup, user }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const enabled = isAdmin(user);

  return (
    <>
      <ActionMenuButton
        id="BulkUpload"
        disabled={!enabled}
        onClick={() => setIsOpen(true)}
      >
        <div
          className={styles.withIcon}
          data-testid="bulk-upload-control"
          aria-disabled={!enabled}
        >
          <img
            className={enabled ? styles.icon : `${styles.icon} ${styles.disabledIcon}`}
            src={uploadIcon}
            alt="Bulk upload"
          />
          <span
            className={enabled ? undefined : styles.disabledText}
            style={{ textTransform: "capitalize" }}
          >
            Bulk Upload
          </span>
        </div>
      </ActionMenuButton>
      <BulkUploadDialog
        domainGroup={domainGroup}
        user={user}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

export default BulkUploadControl;
